/**
 * BusinessDashboardPage — Phase 3
 * ────────────────────────────────
 * The unified Business Workspace shell.  Renders category-aware panels
 * (Overview / Listings / Analytics / Reviews / Employees / Branches /
 * Verification / Subscription / Profile) inside a fixed dark sidebar +
 * scrollable content.
 *
 * Route: /business/:businessId
 * Route: /business/:businessId/:section
 *
 * DOES NOT touch or replace the existing /dashboard page.
 */
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, useParams } from "react-router-dom";
import {
  BarChart3, Boxes, Building2, Calendar, ClipboardCheck, ClipboardList, CreditCard,
  Gavel, Home as HomeIcon, LayoutGrid, ListChecks, MessageSquare, Package,
  Percent, Shield, Star, Truck, Users, Wallet, Wrench, Tag, Lock, AlertTriangle, Clock, ArrowLeft, DownloadCloud,
} from "lucide-react";

import { apiClient } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useI18n } from "../../lib/i18n";
import {
  isBusinessVerified, ONBOARDING_SECTIONS, STATUS_META, TONE_COLORS,
} from "../../components/business/verificationStatus";

import DashOverview from "../../components/business/panels/DashOverview";
import DashListings from "../../components/business/panels/DashListings";
import DashProducts from "../../components/business/panels/DashProducts";
import DashAnalytics from "../../components/business/panels/DashAnalytics";
import DashEmployees from "../../components/business/panels/DashEmployees";
import DashBranches from "../../components/business/panels/DashBranches";
import DashVerification from "../../components/business/panels/DashVerification";
import DashSubscription from "../../components/business/panels/DashSubscription";
import DashReviews from "../../components/business/panels/DashReviews";
import DashProfile from "../../components/business/panels/DashProfile";
import DashCategoryPanel from "../../components/business/panels/DashCategoryPanel";
import DashMessages from "../../components/business/panels/DashMessages";
import DashBookings from "../../components/business/panels/DashBookings";
import DashRequests from "../../components/business/panels/DashRequests";
import DashAuctions from "../../components/business/panels/DashAuctions";
import DashImports from "../../components/business/panels/DashImports";
import { FEATURES } from "../../lib/features";

/* ─── Sections definition (order-preserving) ─────────────────────── */
const CORE_SECTIONS = [
  { key: "overview",     labelAr: "الرئيسية",        labelEn: "Overview",     icon: HomeIcon,     perm: "business.read"       },
  { key: "products",     labelAr: "المنتجات والخدمات", labelEn: "Products & Services", icon: Tag,     perm: "content.read"        },
  { key: "listings",     labelAr: "إعلاناتي",        labelEn: "Listings",     icon: LayoutGrid,   perm: "content.read"        },
  { key: "imports",      labelAr: "استيراد الإعلانات", labelEn: "Import listings", icon: DownloadCloud, perm: "content.read"     },
  { key: "messages",     labelAr: "الرسائل",          labelEn: "Messages",     icon: MessageSquare, perm: "business.read"      },
  { key: "bookings",     labelAr: "الحجوزات",         labelEn: "Bookings",     icon: Calendar,     perm: "business.read"       },
  { key: "requests",     labelAr: "طلبات العملاء",    labelEn: "Customer Requests", icon: ClipboardList, perm: "business.read"  },
  { key: "auctions",     labelAr: "المزادات",         labelEn: "Auctions",     icon: Gavel,        perm: "business.read"       },
  { key: "analytics",    labelAr: "التحليلات",       labelEn: "Analytics",    icon: BarChart3,    perm: "analytics.view"      },
  { key: "reviews",      labelAr: "التقييمات",       labelEn: "Reviews",      icon: Star,         perm: "business.read"       },
  { key: "employees",    labelAr: "الموظفون",        labelEn: "Employees",    icon: Users,        perm: "employee.read"       },
  { key: "branches",     labelAr: "الفروع",          labelEn: "Branches",     icon: Building2,    perm: "branch.read"         },
  { key: "verification", labelAr: "التوثيق",         labelEn: "Verification", icon: ClipboardCheck, perm: "business.verify"   },
  { key: "subscription", labelAr: "الاشتراك",        labelEn: "Subscription", icon: CreditCard,   perm: "business.subscription" },
  { key: "profile",      labelAr: "الملف التعريفي",  labelEn: "Profile",      icon: Shield,       perm: "business.update"     },
];

/* ─── Category-specific extra section ────────────────────────────── */
const CATEGORY_SECTION = {
  rental_company:      { labelAr: "الأسطول والحجوزات",  labelEn: "Fleet & Reservations", icon: Truck    },
  insurance_company:   { labelAr: "طلبات التأمين",       labelEn: "Insurance Requests",   icon: Shield   },
  insurance_broker:    { labelAr: "طلبات التأمين",       labelEn: "Insurance Requests",   icon: Shield   },
  finance_company:     { labelAr: "طلبات التمويل",       labelEn: "Finance Requests",     icon: Wallet   },
  leasing_company:     { labelAr: "عقود التأجير التمويلي", labelEn: "Leasing Contracts",  icon: Wallet   },
  bank:                { labelAr: "المنتجات والطلبات",    labelEn: "Products & Requests",  icon: Wallet   },
  fintech:             { labelAr: "المنتجات الرقمية",     labelEn: "Digital Products",     icon: Wallet   },
  bnpl_service:        { labelAr: "خطط التقسيط",         labelEn: "Installment Plans",    icon: Wallet   },
  financing_platform:  { labelAr: "المنتجات التمويلية",   labelEn: "Financing Products",   icon: Wallet   },
  payment_provider:    { labelAr: "حلول الدفع",          labelEn: "Payment Solutions",    icon: CreditCard },
  inspection_center:   { labelAr: "الحجوزات والتقارير",   labelEn: "Bookings & Reports",   icon: Calendar },
  periodic_inspection: { labelAr: "الحجوزات والتقارير",   labelEn: "Bookings & Reports",   icon: Calendar },
  workshop:            { labelAr: "أوامر العمل",         labelEn: "Work Orders",          icon: Wrench   },
  auction_house:       { labelAr: "المزادات",           labelEn: "Auctions",              icon: Gavel    },
  parts_store:         { labelAr: "قطع الغيار",         labelEn: "Parts",                 icon: Boxes    },
  tire_battery_shop:   { labelAr: "الإطارات والبطاريات", labelEn: "Tires & Batteries",    icon: Boxes    },
  salvage:             { labelAr: "المخزون",            labelEn: "Inventory",             icon: Package  },
  transport_company:   { labelAr: "الشحنات",            labelEn: "Shipments",             icon: Truck    },
  recovery_towing:     { labelAr: "طلبات الإنقاذ",       labelEn: "Recovery Jobs",         icon: Truck    },
  car_wash:            { labelAr: "الحجوزات",           labelEn: "Bookings",              icon: Calendar },
  dealer:              { labelAr: "العروض",             labelEn: "Offers",                icon: Percent  },
  official_agency:     { labelAr: "الطرازات",           labelEn: "Models",                icon: LayoutGrid },
};

/* Phase 2F — categories that actually manage vehicle inventory.
   Everything else (insurance, banks, finance, workshops, parts, etc.) does
   NOT show the "إعلاناتي" or "المزادات" sections, since they don't sell cars. */
const INVENTORY_CATEGORIES = new Set([
  "dealer", "official_agency", "rental_company", "auction_house", "salvage",
]);

export default function BusinessDashboardPage() {
  const { businessId, section = "overview" } = useParams();
  const { user } = useAuth();
  const { lang } = useI18n();

  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // iter292 — Live unread-message count scoped to THIS business.
  // Sourced from `/api/conversations/unread-count?business_id=…` which
  // aggregates both legacy and Phase-6 `platform_conversations.unread_by`
  // so the sidebar badge stays in sync with the Header badge. Polled
  // every 10s + on tab focus so new incoming messages surface without
  // a manual refresh, matching the Header cadence.
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (!businessId || !user) { setMsgUnread(0); return; }
    let alive = true;
    const fetchUnread = async () => {
      try {
        const { data } = await apiClient.get("/conversations/unread-count", {
          params: { business_id: businessId },
        });
        if (alive) setMsgUnread(Number(data?.count || 0));
      } catch { /* silent */ }
    };
    fetchUnread();
    const tm = setInterval(fetchUnread, 10000);
    const onFocus = () => fetchUnread();
    const onVis = () => { if (document.visibilityState === "visible") fetchUnread(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      clearInterval(tm);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [businessId, user]);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    setLoading(true);
    apiClient
      .get(`/platform/dashboard/business/${businessId}/home`)
      .then(({ data }) => { if (!cancelled) setHome(data); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          // Business no longer exists (e.g. deleted) — this is not a loading
          // failure, so don't show a raw backend error. Bounce the user to
          // their businesses list instead.
          setNotFound(true);
        } else {
          setError(e?.response?.data?.detail || "Failed to load");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [businessId]);

  const verified = isBusinessVerified(home);

  const sections = useMemo(() => {
    const list = [...CORE_SECTIONS];
    const catCfg = CATEGORY_SECTION[home?.business?.category];
    if (catCfg) {
      list.splice(2, 0, { key: "category", icon: catCfg.icon, labelAr: catCfg.labelAr, labelEn: catCfg.labelEn, perm: "content.read" });
    }
    // Phase 2F — non-inventory businesses (insurance, banks, workshops, etc.)
    // do NOT sell cars. Drop the vehicle-oriented sections for them.
    // Symmetric fix (iter131): inventory businesses (dealer / official_agency
    // / rental_company / auction_house / salvage) DO manage cars — their
    // inventory lives under "إعلاناتي" (listings). The generic "المنتجات
    // والخدمات" panel only handles offer/service/part records and is
    // misleading here (dealers reported "no relation to car products").
    // Hide it for inventory categories so the dashboard focuses on
    // vehicle listings + offers as intended.
    const isInventoryBiz = INVENTORY_CATEGORIES.has(home?.business?.category);
    // iter173 — Individual activities are personal, single-seat workspaces.
    // They must NOT see any of the org-scale surfaces (branches, employees,
    // multi-product catalog, bookings, customer requests, analytics,
    // reviews, category-specific sections). This keeps the dashboard
    // focused on: overview + own listings + messages + verification +
    // subscription + profile — matching how leading marketplaces handle
    // personal seller accounts.
    if (home?.business?.category === "individual") {
      const IND_ALLOWED = new Set(["overview", "listings", "messages", "verification", "subscription", "profile"]);
      return list.filter((s) => IND_ALLOWED.has(s.key));
    }
    let visible = isInventoryBiz
      ? list.filter((s) => s.key !== "products")
      : list.filter((s) => !["listings", "auctions", "imports"].includes(s.key));
    // Auctions temporarily hidden platform-wide (Moyasar compliance).
    const filtered = FEATURES.AUCTIONS_ENABLED
      ? visible
      : visible.filter((s) => s.key !== "auctions" && !(s.key === "category" && home?.business?.category === "auction_house"));
    let permFiltered;
    if (home?.permissions) {
      permFiltered = filtered.filter((s) => !s.perm || home.permissions.includes(s.perm) || home.permissions.includes("platform.manage"));
    } else {
      permFiltered = filtered;
    }
    // Onboarding-to-verification HARD gating — unverified businesses can only
    // access onboarding-related sections (overview / verification / profile).
    // Every operational tab is hidden from the sidebar until admin approves.
    if (!verified) {
      return permFiltered.filter((s) => ONBOARDING_SECTIONS.has(s.key));
    }
    return permFiltered;
  }, [home, verified]);

  if (user === undefined) return null; // wait for auth to hydrate
  if (!user) return <Navigate to="/login" replace />;
  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-[#525252]" data-testid="business-dash-loading">
        <div className="w-6 h-6 border-2 border-[#c9a55c] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (notFound) {
    return <Navigate to="/businesses" replace />;
  }
  if (error || !home) {
    return (
      <div className="max-w-2xl mx-auto my-24 p-8 rounded-2xl bg-red-50 border border-red-200 text-red-900" data-testid="business-dash-error">
        <div className="font-bold mb-2">{lang === "ar" ? "تعذّر تحميل لوحة العمل" : "Could not load dashboard"}</div>
        <div className="text-sm opacity-80">{lang === "ar" ? "حدث خطأ غير متوقع. حاول مرة أخرى لاحقاً." : "An unexpected error occurred. Please try again later."}</div>
      </div>
    );
  }

  // Hard-redirect: if the user hits a gated operational section URL while
  // unverified, silently bounce them to the verification journey.
  if (!verified && section && !ONBOARDING_SECTIONS.has(section)) {
    return <Navigate to={`/business/${businessId}/verification`} replace />;
  }
  // Inventory businesses (dealer / official_agency / rental / auction /
  // salvage) don't have a products panel — redirect to their listings.
  if (section === "products" && INVENTORY_CATEGORIES.has(home?.business?.category)) {
    return <Navigate to={`/business/${businessId}/listings`} replace />;
  }

  const currentSection = sections.find((s) => s.key === section) || sections[0];

  const bizStatus = home?.verification?.business_verification_status || "unverified";
  const statusMeta = STATUS_META[bizStatus] || STATUS_META.unverified;
  const statusColor = TONE_COLORS[statusMeta.tone];

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#fafaf7]" data-testid="business-dashboard-shell">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-[280px_1fr] gap-6 px-4 sm:px-6 lg:px-10 py-6">
        {/* ─── Sidebar ─────────────────────────────────────────────── */}
        <aside
          className="lg:sticky lg:top-24 lg:self-start rounded-2xl overflow-hidden shadow-md"
          style={{ background: "linear-gradient(180deg,#0a0a0a 0%,#141110 60%,#1f1614 100%)" }}
        >
          {/* Business identity */}
          <div className="p-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              {home.business.profile?.logo_url ? (
                <img src={home.business.profile.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover ring-1 ring-white/20" />
              ) : (
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-[#0a0a0a] text-lg"
                  style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}
                >
                  {(home.business.name_ar || "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[#f7eecd] font-semibold truncate" title={home.business.name_ar}>
                  {home.business.name_ar}
                </div>
                <div className="text-[11px] text-[#f7eecd]/60 truncate">{home.business.name_en || home.business.category}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
              {verified ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "rgba(201,165,92,0.15)", color: "#e8c873", border: "1px solid rgba(201,165,92,0.4)" }} data-testid="business-verified-badge">
                  <ClipboardCheck className="w-3 h-3" /> {lang === "ar" ? "موثّق" : "Verified"}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}66` }}
                  data-testid="business-unverified-badge"
                >
                  <Lock className="w-3 h-3" /> {lang === "ar" ? statusMeta.ar : statusMeta.en}
                </span>
              )}
              <span className="text-[10px] text-[#f7eecd]/50">· {home.role}</span>
            </div>
          </div>

          {/* Nav */}
          <nav className="p-3 space-y-0.5" data-testid="business-dash-sidebar">
            {sections.map((s) => (
              <NavLink
                key={s.key}
                to={`/business/${businessId}/${s.key}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${
                  isActive || currentSection.key === s.key
                    ? "bg-white/[0.07] text-[#e8c873]"
                    : "text-[#f7eecd]/80 hover:bg-white/[0.04] hover:text-[#f7eecd]"
                }`}
                data-testid={`business-nav-${s.key}`}
              >
                <s.icon className="w-4 h-4" />
                <span className="flex-1">{lang === "ar" ? s.labelAr : s.labelEn}</span>
                {/* iter292 — Live unread badge for the Messages tab. Uses
                    the same polling cadence as the Header badge. */}
                {s.key === "messages" && msgUnread > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold"
                    data-testid="business-nav-messages-badge"
                    aria-label={lang === "ar" ? `${msgUnread} رسائل غير مقروءة` : `${msgUnread} unread messages`}
                  >
                    {msgUnread > 99 ? "99+" : msgUnread}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* ─── Content ─────────────────────────────────────────────── */}
        <main className="min-w-0">
          {/* Onboarding-to-Verification banner — visible on every non-verification
              page for unverified businesses. On the verification page itself the
              StatusHero already communicates the state, so we keep the banner
              only when the user is elsewhere (overview / profile). */}
          {!verified && currentSection.key !== "verification" && (
            <div
              className="mb-6 rounded-2xl border p-4 sm:p-5 flex flex-wrap items-start gap-4"
              style={{
                background: `linear-gradient(135deg, ${statusColor}14, #fff)`,
                borderColor: `${statusColor}55`,
              }}
              data-testid="onboarding-banner"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${statusColor}22`, border: `1px solid ${statusColor}55` }}
              >
                {["rejected", "suspended", "revoked"].includes(bizStatus)
                  ? <AlertTriangle className="w-5 h-5" style={{ color: statusColor }} />
                  : <Clock className="w-5 h-5" style={{ color: statusColor }} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: statusColor }}>
                  {lang === "ar" ? "حساب النشاط غير مكتمل" : "Business account not activated"}
                </div>
                <div className="text-[15px] font-bold text-[#0a0a0a] mt-0.5" data-testid="onboarding-banner-status">
                  {lang === "ar" ? statusMeta.ar : statusMeta.en}
                </div>
                <div className="text-[13px] text-[#525252] mt-1 leading-relaxed">
                  {lang === "ar" ? statusMeta.description_ar : statusMeta.description_en}
                </div>
              </div>
              <NavLink
                to={`/business/${businessId}/verification`}
                className="shrink-0 px-4 py-2 rounded-lg text-[13px] font-bold flex items-center gap-2"
                style={{ background: "#0a0a0a", color: "#e8c873" }}
                data-testid="complete-verification-cta"
              >
                <ArrowLeft className="w-4 h-4" style={{ transform: lang === "ar" ? "scaleX(1)" : "scaleX(-1)" }} />
                {lang === "ar" ? "استكمال التوثيق" : "Complete verification"}
              </NavLink>
            </div>
          )}

          <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8a6f2e] font-semibold">
                {lang === "ar" ? "مساحة عمل الأنشطة" : "Business Workspace"}
              </div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-[#0a0a0a] mt-1">
                {lang === "ar" ? currentSection.labelAr : currentSection.labelEn}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`/business/${businessId}/profile`}
                className="text-[12px] text-[#525252] hover:text-[#0a0a0a] underline underline-offset-4 hidden sm:inline"
              >
                {lang === "ar" ? "عرض الصفحة العامة" : "View public page"}
              </a>
            </div>
          </div>

          <div className="pb-16">
            {currentSection.key === "overview"     && <DashOverview home={home} />}
            {currentSection.key === "products"     && <DashProducts businessId={businessId} home={home} />}
            {currentSection.key === "category"     && <DashCategoryPanel home={home} />}
            {currentSection.key === "listings"     && <DashListings businessId={businessId} />}
            {currentSection.key === "imports"      && <DashImports businessId={businessId} home={home} />}
            {currentSection.key === "messages"     && <DashMessages businessId={businessId} />}
            {currentSection.key === "bookings"     && <DashBookings businessId={businessId} />}
            {currentSection.key === "requests"     && <DashRequests businessId={businessId} />}
            {currentSection.key === "auctions"     && FEATURES.AUCTIONS_ENABLED && <DashAuctions businessId={businessId} />}
            {currentSection.key === "analytics"    && <DashAnalytics businessId={businessId} />}
            {currentSection.key === "reviews"      && <DashReviews businessId={businessId} />}
            {currentSection.key === "employees"    && <DashEmployees businessId={businessId} />}
            {currentSection.key === "branches"     && <DashBranches businessId={businessId} />}
            {currentSection.key === "verification" && <DashVerification businessId={businessId} home={home} />}
            {currentSection.key === "subscription" && <DashSubscription businessId={businessId} />}
            {currentSection.key === "profile"      && <DashProfile businessId={businessId} home={home} />}
          </div>
        </main>
      </div>
    </div>
  );
}
